import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ViewStyle } from 'react-native';
import { Platform, StyleSheet, View } from 'react-native';
import { HelperText, Text } from 'react-native-paper';
import type { SortableGridRenderItem } from 'react-native-sortables';
import type { DragEndParams } from 'react-native-sortables/dist/typescript/types';

import { useUpdateOrganizationPending } from '@/features/api/hooks/grapevine-organization/useUpdateOrganization';
import { THUMB_SIZE } from '@/features/media/components/MediaImageSized';
import { DetailImageAspectRatioWarning } from '@/features/profile-setting/components/organization-edit/DetailImageAspectRatioWarning';
import { DetailImageGrid } from '@/features/profile-setting/components/organization-edit/DetailImageGrid';
import { DetailImageMenu } from '@/features/profile-setting/components/organization-edit/DetailImageMenu';
import { DetailImageUploadProvider } from '@/features/profile-setting/components/organization-edit/DetailImageUploadContext';
import { FormImageThumbnail } from '@/features/profile-setting/components/organization-edit/FormImageThumbnail';
import type {
  DetailImageField,
  OrganizationEditFormValues
} from '@/features/profile-setting/components/organization-edit/types';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const MAX_IMAGES = 8;

type Props = {
  /** Read-only mode: disables adding, removing and reordering images. */
  disabled?: boolean;
};

export const DetailImageList: FC<Props> = ({ disabled }) => (
  <DetailImageUploadProvider>
    <DetailImageListContent disabled={disabled} />
  </DetailImageUploadProvider>
);

const DetailImageListContent: FC<Props> = ({ disabled: disabledProp }) => {
  const isPending = useUpdateOrganizationPending();
  const disabled = disabledProp || isPending;

  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [isDragging, setIsDragging] = useState(false);
  const { control, trigger } = useFormContext<OrganizationEditFormValues>();
  const { fields, append, remove, move, update } = useFieldArray({
    control,
    name: 'detailImages',
    keyName: 'fieldId'
  });
  const { errors, isSubmitted } = useFormState({
    control,
    name: 'detailImages'
  });

  // Dependent validation (RHF docs): after the array changes, re-validate
  // it — but only once the form has been submitted, so nothing shows before
  // the first submit (mode: 'onSubmit').
  const revalidateIfSubmitted = useCallback(async () => {
    if (isSubmitted) {
      await trigger('detailImages');
    }
  }, [isSubmitted, trigger]);

  const handleRemove = useCallback(
    (index: number) => {
      if (disabled) {
        return;
      }
      remove(index);
      void revalidateIfSubmitted();
    },
    [disabled, remove, revalidateIfSubmitted]
  );

  const handleDropped = useCallback(
    ({ fromIndex, toIndex }: DragEndParams) => {
      setIsDragging(false);
      if (!disabled && fromIndex !== toIndex) move(fromIndex, toIndex);
    },
    [disabled, move]
  );

  // Re-validate after a reorder (only once submitted) without touching the drag
  // callback. Keyed on the field order so it runs after the move settles.
  const fieldOrderKey = fields.map((f) => f.fieldId).join('|');
  useEffect(() => {
    void revalidateIfSubmitted();
  }, [fieldOrderKey, revalidateIfSubmitted]);

  const renderImageTile = useCallback<SortableGridRenderItem<DetailImageField>>(
    ({ item, index }) => (
      <FormImageThumbnail
        entry={item}
        index={index}
        total={fields.length}
        errorMessage={errors.detailImages?.[index]?.message}
        onRemove={handleRemove}
        removeDisabled={disabled}
        style={[
          styles.tile,
          disabled && styles.tileDisabled,
          !disabled && (isDragging ? styles.tileGrabbing : styles.tileGrab)
        ]}
      />
    ),
    [
      disabled,
      errors.detailImages,
      fields.length,
      handleRemove,
      styles,
      isDragging
    ]
  );

  return (
    <View style={styles.wrapper}>
      <Text variant="titleMedium" style={styles.title}>
        {t('sections.images', { count: fields.length, max: MAX_IMAGES })}
      </Text>

      <DetailImageGrid
        fields={fields}
        disabled={disabled}
        renderTile={renderImageTile}
        onDropped={handleDropped}
        onDragStart={() => setIsDragging(true)}
      />

      {fields
        .map((field, index) => ({
          fieldId: field.fieldId,
          index,
          message: errors.detailImages?.[index]?.message
        }))
        .filter((entry) => !!entry.message)
        .map((entry) => (
          <HelperText
            key={entry.fieldId}
            type="error"
            visible
            style={styles.errorText}
          >
            {t('errors.imageErrorWithIndex', {
              index: entry.index + 1,
              message: entry.message
            })}
          </HelperText>
        ))}
      <DetailImageAspectRatioWarning />
      <DetailImageMenu fields={fields} append={append} update={update} />
    </View>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    wrapper: { gap: 8 },
    tile: { width: THUMB_SIZE, height: THUMB_SIZE },
    tileDisabled: { opacity: 0.6 },
    tileGrab: Platform.select({
      web: { cursor: 'grab' },
      default: {}
    }) as ViewStyle,
    tileGrabbing: Platform.select({
      web: { cursor: 'grabbing' },
      default: {}
    }) as ViewStyle,
    title: { color: theme.colors.onSurface },
    errorText: { marginTop: -4 }
  });
